import type { GQL_ID, NestedFilter } from '@@partner:graphql/types'

import React, { useCallback, useState } from 'react'
import { Button, Modal, Panel, FlexboxGrid, Pagination, SelectPicker, Checkbox } from 'rsuite'
import PlusIcon from '@rsuite/icons/Plus'

import { useQueryProducts } from '@@partner:graphql/query/catalog/products.gql'
import { useMutateBulkUpdateProducts } from '@@partner:graphql/mutation/catalog/bulkUpdateProducts'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { notifyOk, notifyError } from '@@partner:helpers/notification'

import ProductsFilter from '../product/grid/ProductsFilter'
import ProductsTable from '../product/grid/ProductsTable'

export interface ProductGroupAddProductsProps {
  groupId: GQL_ID
  onProductsAdded?: () => void
}

const DEFAULT_SHOW_SETTINGS = {
  shopsIds: []
}

export default function ProductGroupAddProducts ({
  groupId,
  onProductsAdded
}: ProductGroupAddProductsProps) {
  const [ showModal, setShowModal ] = useState(false)
  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })
  const [ selected, setSelected ] = useState<GQL_ID[]>([])
  const [ useFilter, setUseFilter ] = useState(false)
  const [ replaceProducts, setReplaceProducts ] = useState(false)

  const {
    page,
    limit,
    defaultLimitOptions,
    tableSort,
    handlePageSelect,
    handleLimitSelect,
    handleSortColumn
  } = useGridHelper()

  const { data, loading } = useQueryProducts({
    pager: { page, limit },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    },
    filter
  })

  const [ bulkUpdate, { loading: syncing } ] = useMutateBulkUpdateProducts()

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
      handlePageSelect(1)
    },
    [ handlePageSelect ]
  )

  const handleSelectProduct = (id: GQL_ID) => {
    const includes = selected.includes(id)
    if (includes) {
      setSelected((selected) => selected.filter((val) => val !== id))
    }
    else {
      setSelected((selected) => [ ...selected, id ])
    }
  }

  const handleOpen = () => {
    setShowModal(true)
    setSelected([])
    setUseFilter(false)
    setReplaceProducts(false)
  }

  const handleClose = () => {
    setShowModal(false)
  }

  const hasFilters = filter.and && filter.and.some((f) => f.op !== 'unwrap')

  const handleAddProducts = async () => {
    try {
      let targetFilter: NestedFilter

      if (useFilter && hasFilters) {
        // Используем текущий фильтр
        targetFilter = filter
      }
      else if (selected.length > 0) {
        // Фильтр по выбранным ID
        targetFilter = {
          fields: [ 'id' ],
          op: 'contains',
          value: selected.map(id => id.toString())
        }
      }
      else {
        notifyError('Выберите товары или используйте фильтр')
        return
      }

      await bulkUpdate({
        variables: {
          filter: targetFilter,
          productInfo: {
            productGroup: groupId,
            replaceProductGroup: replaceProducts
          }
        }
      })

      notifyOk(replaceProducts ? 'Товары заменены' : 'Товары добавлены')
      handleClose()
      onProductsAdded?.()
    }
    catch (error) {
      notifyError('Ошибка при добавлении товаров')
    }
  }

  const products = data?.products.edges ?? []
  const pageInfo = data?.products.pageInfo
  const canAdd = (useFilter && hasFilters) || selected.length > 0

  return (
    <>
      <Button
        appearance="primary"
        color="green"
        onClick={handleOpen}
        startIcon={<PlusIcon />}
      >
        Добавить товары
      </Button>

      <Modal
        open={showModal}
        onClose={handleClose}
        size="full"
        overflow={false}
      >
        <Modal.Header>
          <Modal.Title>Добавить товары в группу</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <ProductsFilter
            onFilterChanged={handleFilterChanged}
            showBulkUpdate={false}
            onShowBulkUpdate={() => {}}
            canBulkUpdate={false}
          />

          <Panel
            shaded
            className="my-4"
            style={{ backgroundColor: 'white', padding: '16px' }}
          >
            <FlexboxGrid justify="space-between" align="middle">
              <FlexboxGrid.Item>
                <FlexboxGrid align="middle" style={{ gap: 16 }}>
                  {hasFilters && (
                    <Checkbox
                      checked={useFilter}
                      onChange={(_, checked) => {
                        setUseFilter(checked)
                        if (checked) setSelected([])
                      }}
                    >
                      Применить ко всем по фильтру ({pageInfo?.total ?? 0} товаров)
                    </Checkbox>
                  )}
                  {!useFilter && selected.length > 0 && (
                    <span>Выбрано: {selected.length} товаров</span>
                  )}
                  <Checkbox
                    checked={replaceProducts}
                    onChange={(_, checked) => setReplaceProducts(checked)}
                  >
                    Заменить существующие товары
                  </Checkbox>
                </FlexboxGrid>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Button
                  appearance="primary"
                  color="green"
                  size="lg"
                  onClick={handleAddProducts}
                  loading={syncing}
                  disabled={!canAdd}
                >
                  {replaceProducts ? 'Заменить товары' : 'Добавить'}
                </Button>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Panel>

          <ProductsTable
            tableSort={tableSort}
            loading={loading}
            products={products}
            generateEditUrl={() => ''}
            onSortColumn={handleSortColumn}
            showSettings={DEFAULT_SHOW_SETTINGS}
            selected={useFilter ? [] : selected}
            onSelect={handleSelectProduct}
          />

          <FlexboxGrid
            justify="space-between"
            align="middle"
            style={{ marginTop: 10 }}
          >
            <Pagination
              last
              first
              next
              prev
              ellipsis
              activePage={page}
              pages={pageInfo?.lastPage}
              total={pageInfo?.total ?? 0}
              maxButtons={10}
              onSelect={handlePageSelect}
            />
            <SelectPicker
              value={limit}
              data={defaultLimitOptions.map((key) => ({ value: key, label: key }))}
              cleanable={false}
              searchable={false}
              onChange={handleLimitSelect}
            />
          </FlexboxGrid>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleClose} appearance="subtle">
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
